import { useMemo, useState } from "react";
import { rankingData, rankingTabs, type HotItem } from "@/mocks/trends";

function RankRow({ item }: { item: HotItem }) {
  const trendMap = {
    up: { icon: "ri-arrow-up-line", color: "text-primary-500", label: "上升" },
    down: { icon: "ri-arrow-down-line", color: "text-secondary-500", label: "回落" },
    new: { icon: "ri-sparkling-line", color: "text-accent-500", label: "新上榜" },
    flat: { icon: "ri-subtract-line", color: "text-foreground-400", label: "持平" },
  } as const;
  const t = trendMap[item.trend];

  return (
    <div className="flex items-start gap-3 px-1 py-3 md:items-center">
      <span
        className={`w-9 shrink-0 font-heading text-2xl font-bold md:text-3xl ${
          item.rank === 1 ? "text-primary-500" : item.rank === 2 ? "text-accent-500" : item.rank === 3 ? "text-accent-400" : "text-foreground-300"
        }`}
      >
        {String(item.rank).padStart(2, "0")}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h4 className="text-sm font-bold text-foreground-950 md:text-base">{item.title}</h4>
          {item.tag && (
            <span
              className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
                item.tag === "爆" ? "bg-primary-500 text-background-50" : item.tag === "热" ? "bg-accent-500 text-background-50" : "bg-secondary-500 text-background-50"
              }`}
            >
              {item.tag}
            </span>
          )}
        </div>
        <p className="mt-0.5 hidden truncate text-xs text-foreground-500 sm:block">{item.summary}</p>
        <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-background-200">
          <div
            className="h-full rounded-full bg-gradient-to-r from-primary-400 to-primary-600"
            style={{ width: `${item.hot}%` }}
          ></div>
        </div>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-0.5">
        <span className="flex items-center gap-1 font-heading text-base font-semibold text-foreground-900">{item.heat}</span>
        <span className={`flex items-center gap-0.5 text-xs ${t.color}`} title={t.label}>
          <i className={`${t.icon}`}></i>
          <span className="hidden md:inline">{t.label}</span>
        </span>
      </div>
    </div>
  );
}

export default function RankingBoard() {
  const [tab, setTab] = useState(rankingTabs[0]);
  const list = useMemo(() => rankingData[tab] ?? rankingData[rankingTabs[0]], [tab]);

  return (
    <section id="ranking" className="w-full scroll-mt-20">
      <div className="mx-auto w-full max-w-6xl px-4 py-10 md:px-6 md:py-14">
        <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-bold text-primary-700">
              <i className="ri-trophy-line"></i> HOT RANKING
            </span>
            <h2 className="mt-3 text-2xl font-bold text-foreground-950 md:text-4xl">热度排行</h2>
            <p className="mt-2 text-sm text-foreground-600 md:text-base">按点击量与讨论量实时排序，谁在风口浪尖一眼看清</p>
          </div>
        </div>

        {/* 分段切换 */}
        <div className="mt-6 flex flex-wrap gap-2 rounded-full bg-background-100 p-1 sm:inline-flex">
          {rankingTabs.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition ${
                tab === t ? "bg-primary-500 text-background-50 shadow-sm" : "text-foreground-700 hover:bg-background-200"
              }`}
            >
              {t}榜
            </button>
          ))}
        </div>

        {/* 列表卡片 */}
        <div className="mt-6 overflow-hidden rounded-2xl border border-background-200 bg-background-100">
          <div className="divide-y divide-background-200 px-4 md:px-6">
            {list.map((item) => (
              <div key={`${tab}-${item.rank}`} className="group cursor-pointer transition hover:bg-background-50">
                <RankRow item={item} />
              </div>
            ))}
          </div>
        </div>
        <p className="mt-3 text-center text-xs text-foreground-500">
          榜单实时更新 · 数据聚合自全网公开讨论 · 热度不代表事实判断
        </p>
      </div>
    </section>
  );
}